/*
 * Variant availability fix — Zoccola Calzature (v4.3)
 * ----------------------------------------------
 * Problem: when a customer picks a color, sizes that are sold out for that
 * color should be grayed out / struck through.
 *
 * v4 change: v2/v3 asked the theme's own "section render" endpoint for
 * availability. On products with more than a couple of variants that
 * endpoint itself reports the wrong answer — confirmed directly against
 * Shopify (bypassing this script entirely): for a real product on this
 * store, size 36 in color NERO has 0 stock and is set to NOT oversell, yet
 * the theme's own server-side calculation reported it as available. That
 * is a bug in the theme's Liquid code, not something this script can shim
 * around by parsing that same output differently.
 *
 * v4 fixes this by no longer trusting that computation at all. Instead, on
 * every selection change it fetches Shopify's own product JSON
 * (yoursite.com/products/handle.js), which lists every variant with its
 * real, always-accurate available flag, and works out sold-out sizes
 * directly from that — the same data Shopify itself uses to decide whether
 * an order can be placed.
 *
 * Also kept from v3: nothing is ever shown as sold out before the customer
 * makes a real selection, and this is continuously enforced (not just once
 * on load) to also cover products opened via a direct ?variant=... link.
 *
 * v4.1 fixes two bugs found on the live store:
 * - getSelectedValue() used to also accept a leftover data-selected
 *   attribute as "this is selected". The theme writes that attribute once
 *   on the default/first value of each option and does not keep it in
 *   sync while only some options have been picked, so this script kept
 *   computing availability against that default color/size instead of
 *   what the customer actually clicked, until every option was chosen.
 *   Fixed to trust only the real checked/selected state.
 * - The gallery image used to switch only once every option (including
 *   size) had been picked, because the theme's own image sync only reacts
 *   once a full variant is resolved. Added updateGalleryImage() below,
 *   which switches to the picked color's photo as soon as any option
 *   changes, reusing the same accurate product JSON already fetched here.
 *
 * v4.2 fixes updateGalleryImage() itself: it was reading
 * variant.featured_image.id, but Shopify's product JSON has two different
 * ID spaces for the same photo — the legacy "image" id (featured_image.id)
 * and the "media" id (featured_media.id). The theme's own gallery markup
 * is keyed by the media id (data-media-id), and that is also what the
 * theme's native code uses when it does this same lookup. Using the
 * legacy image id meant the gallery item was never found, so the photo
 * silently never changed. Fixed to use variant.featured_media.id, which
 * was confirmed to match the gallery's data-media-id values exactly.
 *
 * v4.3 makes findGalleryVariant() tolerant of a real data gap found on
 * this store: for some products, the sizes that are actually in stock for
 * a color are NOT the ones connected to that color's photo in Shopify
 * (only some out-of-stock sizes are). Picking "the in-stock variant"
 * could therefore land on a variant with no photo at all, so the image
 * silently never updated for that color. Now it prefers an in-stock
 * variant that has a photo, then falls back to ANY variant of that same
 * color that has one (in stock or not) before giving up — the photo
 * represents the color, not the specific size.
 *
 * This script does not modify any original theme file and can be removed
 * at any time by deleting this file and the one line that includes it in
 * layout/theme.liquid.
 */
(function () {
  'use strict';

  var timers = new WeakMap();
  function schedule(key, fn, wait) {
    if (timers.has(key)) clearTimeout(timers.get(key));
    timers.set(key, setTimeout(fn, wait));
  }

  // Containers where a real, interaction-driven refresh has completed.
  // Until that happens, we keep stripping any "disabled" styling that
  // shows up from any source (including the theme's own native code).
  var settled = new WeakSet();

  function stripPremature(container) {
    if (settled.has(container)) return;
    container.querySelectorAll('.product-variant-value.disabled').forEach(function (el) {
      el.classList.remove('disabled');
    });
    container.querySelectorAll('.product-variant__item.disabled').forEach(function (el) {
      el.classList.remove('disabled');
    });
  }

  function getRows(container) {
    // One row per product option (Taglia, Colore, ...), in the same order
    // Shopify lists them in — so row index (0-based) + 1 = option position,
    // matching option1/option2/option3 on each variant.
    return Array.from(container.querySelectorAll('[data-js-product-variant]'));
  }

  function getRowValueEls(row) {
    return Array.from(row.querySelectorAll('.product-variant-value'));
  }

  function getSelectedValue(row) {
    var valueEls = getRowValueEls(row);
    for (var i = 0; i < valueEls.length; i++) {
      var el = valueEls[i];
      if (el.tagName === 'OPTION') {
        if (el.selected) return el.value;
      } else if (el.checked) {
        return el.value;
      }
    }
    return null;
  }

  function optionValue(variant, position) {
    if (position === 1) return variant.option1;
    if (position === 2) return variant.option2;
    if (position === 3) return variant.option3;
    return undefined;
  }

  function isAvailable(variants, fixed, position, candidate) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v.available) continue;
      if (optionValue(v, position) !== candidate) continue;
      var ok = true;
      for (var p in fixed) {
        if (Object.prototype.hasOwnProperty.call(fixed, p) && optionValue(v, Number(p)) !== fixed[p]) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  function findExactVariant(variants, selections, rowCount) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var p = 1; p <= rowCount; p++) {
        if (selections[p] !== undefined && optionValue(v, p) !== selections[p]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  function applyFromProductData(container, product) {
    if (!product || !product.variants) return;
    var rows = getRows(container);
    var selections = {}; // position -> selected value text, only for rows with a selection
    rows.forEach(function (row, idx) {
      var sel = getSelectedValue(row);
      if (sel !== null) selections[idx + 1] = sel;
    });

    rows.forEach(function (row, idx) {
      var pos = idx + 1;
      var fixed = {};
      for (var p in selections) {
        if (Number(p) !== pos) fixed[p] = selections[p];
      }
      getRowValueEls(row).forEach(function (el) {
        var candidate = el.value;
        var available = isAvailable(product.variants, fixed, pos, candidate);
        var wrapper = el.closest('.product-variant__item');
        el.setAttribute('data-available', available ? 'true' : 'false');
        if (available) {
          el.classList.remove('disabled');
          if (wrapper) wrapper.classList.remove('disabled');
        } else if (!el.hasAttribute('data-product-url')) {
          el.classList.add('disabled');
          if (wrapper) wrapper.classList.add('disabled');
        }
      });
    });

    if (Object.keys(selections).length === rows.length) {
      var exact = findExactVariant(product.variants, selections, rows.length);
      if (exact) {
        rows.forEach(function (row, idx) {
          var sel = selections[idx + 1];
          getRowValueEls(row).forEach(function (el) {
            if (el.value === sel) el.setAttribute('data-variant-id', exact.id);
          });
        });
      }
    }
  }

  function firstWithImage(list) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].featured_media) return list[i];
    }
    return null;
  }

  function findGalleryVariant(container, product) {
    var rows = getRows(container);
    var selections = {};
    rows.forEach(function (row, idx) {
      var sel = getSelectedValue(row);
      if (sel !== null) selections[idx + 1] = sel;
    });
    if (!Object.keys(selections).length) return null;
    var candidates = product.variants.filter(function (v) {
      for (var p in selections) {
        if (Object.prototype.hasOwnProperty.call(selections, p) && optionValue(v, Number(p)) !== selections[p]) return false;
      }
      return true;
    });
    if (!candidates.length) return null;
    var withAvailable = candidates.filter(function (v) { return v.available; });
    // Prefer an in-stock candidate that actually has a photo. Some products
    // have gaps where Shopify's own data connects the color photo to only
    // some sizes of that color — often the sizes that happen to be sold
    // out — leaving the in-stock size(s) with no image of their own. Since
    // the photo represents the color, not the size, fall back to any other
    // size of the same color that does have one rather than showing nothing.
    return firstWithImage(withAvailable) || firstWithImage(candidates) || withAvailable[0] || candidates[0];
  }

  // Switch the gallery to the picked color's photo as soon as a color (or
  // any option) is chosen, without waiting for every option to be picked.
  function updateGalleryImage(container, product) {
    var variant = findGalleryVariant(container, product);
    if (!variant || !variant.featured_media) return;
    var root = container.closest('[data-js-product-component]') || document;
    var gallery = root.querySelector('[data-js-product-gallery]');
    if (!gallery) return;
    var item = gallery.querySelector('.product-gallery-item[data-media-id="' + variant.featured_media.id + '"]');
    if (!item) return;
    var slider = gallery.querySelector('css-slider');
    if (slider && slider.sliderEnabled && typeof slider.changeSlide === 'function') {
      slider.changeSlide(item.dataset.index);
    }
  }

  function refresh(container) {
    var url = container.dataset.url;
    if (!url) return;
    var base = url.split('?')[0];
    fetch(base + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        // From this point on, real fetched data governs this container —
        // stop suppressing "disabled" styling for it.
        settled.add(container);
        applyFromProductData(container, product);
        updateGalleryImage(container, product);
      })
      .catch(function () {});
  }

  // The only trigger for a real availability check: the customer actually
  // changing a color or size selection.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches) return;
    if (!target.matches('.product-variant-value, [data-js-product-variant-container]')) return;
    var container = target.closest('product-variants');
    if (!container) return;
    schedule(container, function () { refresh(container); }, 150);
  });

  // Continuously guard against the theme's own (buggy) variant code marking
  // something sold out before the customer has made a real selection. This
  // can happen a few seconds after the page loads (a delayed background
  // preload triggered when a variant is already selected via a shared
  // product link, e.g. ?variant=...), not just immediately on load, so a
  // one-off cleanup right after DOMContentLoaded is not enough by itself.
  new MutationObserver(function (mutations) {
    var containers = new Set();
    mutations.forEach(function (m) {
      var el = m.target;
      if (!el || el.nodeType !== 1 || !el.classList) return;
      if (el.classList.contains('product-variant-value') || el.classList.contains('product-variant__item')) {
        var container = el.closest('product-variants');
        if (container) containers.add(container);
      }
    });
    containers.forEach(stripPremature);
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true
  });

  function initAll(root) {
    (root || document).querySelectorAll('product-variants').forEach(stripPremature);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
})();